import { type ToolSpec, n, s, fmt, money } from "./types";

export const DIGITAL_TOOLS: ToolSpec[] = [
  {
    slug: "aspect-ratio-pixel-calculator",
    cluster: "digital",
    name: "Aspect Ratio & Pixel Crop",
    h1: "Video Aspect Ratio Calculator (16:9, 9:16, 4:5 Pixel Dimensions)",
    title: "Aspect Ratio Calculator — 16:9, 9:16, 4:5 Pixel Sizes",
    description:
      "Convert any width or height into the matching pixel dimensions for 16:9, 9:16, 1:1, 4:5 and 21:9, with safe-zone guidance for Reels, Shorts and TikTok.",
    keywords: [
      "video aspect ratio calculator 16 9 9 16 4 5 pixels",
      "convert 1920x1080 to vertical 9 16 dimensions",
      "instagram reel resolution pixel size",
      "youtube shorts safe zone dimensions",
    ],
    inputs: [
      { id: "ratio", label: "Target aspect ratio", type: "select", def: "16:9", options: [
        { value: "16:9", label: "16:9 — landscape video" },
        { value: "9:16", label: "9:16 — Reels, Shorts, TikTok" },
        { value: "1:1", label: "1:1 — square feed post" },
        { value: "4:5", label: "4:5 — Instagram portrait" },
        { value: "21:9", label: "21:9 — cinematic ultrawide" },
        { value: "4:3", label: "4:3 — classic / iPad" },
        { value: "3:2", label: "3:2 — DSLR photo" },
      ] },
      { id: "lock", label: "Lock dimension", type: "select", def: "width", options: [{ value: "width", label: "Set width, solve height" }, { value: "height", label: "Set height, solve width" }] },
      { id: "value", label: "Known dimension", type: "number", def: "1080", unit: "px", min: 1 },
    ],
    compute: (v) => {
      const [rw, rh] = s(v, "ratio", "16:9").split(":").map(Number);
      const val = n(v, "value", 1);
      const width = s(v, "lock") === "width" ? val : Math.round((val * rw) / rh);
      const height = s(v, "lock") === "width" ? Math.round((val * rh) / rw) : val;
      const mp = (width * height) / 1_000_000;
      const presets = [
        ["16:9 1080p", 1920, 1080], ["16:9 4K", 3840, 2160], ["9:16 Reels", 1080, 1920],
        ["1:1 square", 1080, 1080], ["4:5 portrait", 1080, 1350], ["21:9 cinema", 2560, 1080],
      ] as [string, number, number][];
      return {
        cards: [
          { label: "Pixel dimensions", value: `${width} × ${height}`, sub: `${s(v, "ratio")} exact`, accent: true },
          { label: "Megapixels", value: `${fmt(mp, 2)} MP`, sub: `${fmt(width * height)} total pixels` },
          { label: "Ratio decimal", value: fmt(width / height, 4), sub: `${rw}:${rh} = ${fmt(rw / rh, 4)}` },
          { label: "Orientation", value: width > height ? "Landscape" : width < height ? "Portrait" : "Square", sub: `${fmt(Math.max(width, height) / Math.min(width, height), 2)}:1 long edge` },
        ],
        table: {
          title: "Your source scaled into every common ratio",
          head: ["Format", "Standard size", "Your longest edge scaled", "Crop needed from your frame"],
          rows: presets.map(([label, pw, ph]) => {
            const scale = Math.max(width, height) / Math.max(pw, ph);
            return [label, `${pw} × ${ph}`, `${Math.round(pw * scale)} × ${Math.round(ph * scale)}`, `${fmt(Math.abs(1 - (pw / ph) / (width / height)) * 100, 1)}%`];
          }),
        },
        summary: `Divisible by 2: ${width % 2 === 0 && height % 2 === 0 ? "yes — safe for H.264 encoding" : "no — round to even numbers before export"}.`,
      };
    },
    intro:
      "Aspect ratio is a shape, not a size: 1280×720 and 3840×2160 are both 16:9. Problems start when you crop between shapes, because moving a 16:9 landscape frame into a 9:16 vertical one discards about eighty-two percent of the horizontal image. Knowing the exact pixel target before shooting is what prevents that loss.",
    sections: [
      { h: "Why even numbers matter", body: "H.264 and H.265 encode in macroblocks of sixteen pixels and require even dimensions at minimum. Odd numbers such as 1081×607 either fail to encode or get silently padded, which introduces a one-pixel green or black edge visible on some players. Always round to even, ideally to multiples of four." },
      { h: "Safe zones on vertical platforms", body: "A 1080×1920 vertical video is not fully visible on any of the big platforms. Instagram Reels overlays the caption and action buttons over roughly the bottom 320 pixels and the top 220. TikTok reserves about 480 pixels at the bottom on some layouts. Keep text and faces inside the central 1080×1220 region." },
      { h: "Upscaling versus reframing", body: "Scaling a 1080p master to 4K adds no detail — it only adds file size and, on some platforms, a higher-quality encoding ladder. Reframing, by contrast, genuinely changes composition. If you must deliver both landscape and vertical, shoot 4K landscape and crop vertically, which preserves 1080p resolution in the vertical output." },
    ],
    staticTable: {
      title: "Platform delivery specifications",
      head: ["Platform", "Ratio", "Recommended pixels", "Max length", "Notes"],
      rows: [
        ["YouTube (standard)", "16:9", "3840 × 2160", "12 h", "4K improves the encode ladder"],
        ["YouTube Shorts", "9:16", "1080 × 1920", "3 min", "Square crops get pillarboxed"],
        ["Instagram Reels", "9:16", "1080 × 1920", "3 min", "Keep text in the middle 60%"],
        ["Instagram feed", "4:5", "1080 × 1350", "60 s", "Tallest allowed feed ratio"],
        ["TikTok", "9:16", "1080 × 1920", "10 min", "Heavy UI overlay at the bottom"],
        ["X / Twitter", "16:9", "1280 × 720", "2 min 20 s", "Autoplay muted"],
        ["LinkedIn", "1:1 or 16:9", "1080 × 1080", "10 min", "Square performs best in feed"],
      ],
    },
    faqs: [
      { q: "What pixel size is 9:16 for Reels and Shorts?", a: "1080 × 1920 is the standard delivery size. Anything taller than 1920 gets downscaled, and anything narrower than 1080 is upscaled with visible softness." },
      { q: "How do I convert 1920×1080 to a vertical crop?", a: "A full-height vertical crop from 1080p landscape is 608 × 1080, which is below platform standard. Shoot or export 4K landscape and crop to 1080 × 1920 instead." },
      { q: "Does aspect ratio affect video quality?", a: "Not directly, but cropping does — every crop discards pixels. Match the shooting ratio to the primary delivery platform and treat other ratios as secondary crops." },
    ],
    related: ["reading-time-calculator", "file-download-time-calculator", "storage-unit-converter", "social-engagement-rate-calculator"],
  },

  {
    slug: "reading-time-calculator",
    cluster: "digital",
    name: "Reading & Speech Duration",
    h1: "Reading Time Calculator (Word Count to Minutes for Reading and Speech)",
    title: "Reading Time & Speech Duration Calculator by Word Count",
    description:
      "Convert a word count into silent reading time, presentation speaking time and audiobook narration length, with WPM control and a speech-length reference table.",
    keywords: [
      "reading time calculator word count to minutes speech",
      "how long does it take to read 1000 words aloud",
      "words per minute speech for a 10 minute presentation",
      "audiobook narration words per hour",
    ],
    inputs: [
      { id: "words", label: "Word count", type: "number", def: "1200", min: 1, step: 50 },
      { id: "readwpm", label: "Silent reading speed", type: "number", def: "238", unit: "wpm", min: 80, max: 800 },
      { id: "speakwpm", label: "Speaking speed", type: "number", def: "140", unit: "wpm", min: 60, max: 250 },
      { id: "pause", label: "Pause / Q&A allowance", type: "number", def: "10", unit: "%", min: 0, max: 60 },
    ],
    compute: (v) => {
      const w = n(v, "words", 1);
      const read = w / n(v, "readwpm", 238);
      const speak = (w / n(v, "speakwpm", 140)) * (1 + n(v, "pause") / 100);
      const mmss = (mins: number) => `${Math.floor(mins)}:${String(Math.round((mins % 1) * 60)).padStart(2, "0")}`;
      return {
        cards: [
          { label: "Silent reading time", value: mmss(read), sub: `${n(v, "readwpm", 238)} wpm average adult`, accent: true },
          { label: "Spoken duration", value: mmss(speak), sub: `${n(v, "speakwpm", 140)} wpm + ${n(v, "pause")}% pauses` },
          { label: "Audiobook length", value: mmss(w / 155), sub: "155 wpm narration standard" },
          { label: "Skim time", value: mmss(w / 450), sub: "450 wpm scanning speed" },
        ],
        table: {
          title: "Duration at different speeds",
          head: ["Speed", "Words per minute", "Duration", "Typical context"],
          rows: [
            ["Slow narration", 110, mmss(w / 110), "Technical audio, learners"],
            ["Conversational speech", 140, mmss(w / 140), "Presentations, podcasts"],
            ["Fast presenter", 170, mmss(w / 170), "Energetic keynote"],
            ["Audiobook standard", 155, mmss(w / 155), "Commercial narration"],
            ["Average silent reading", 238, mmss(w / 238), "Blog and article reading"],
            ["Fast reader", 350, mmss(w / 350), "Experienced non-fiction reader"],
            ["Skimming", 600, mmss(w / 600), "Scanning for keywords"],
          ],
        },
        summary: `A ${fmt(w)} word piece is roughly ${fmt(w / 500, 1)} pages of double-spaced A4.`,
      };
    },
    intro:
      "Reading and speaking happen at completely different speeds, and conflating them is why scripted talks overrun. Meta-analysis of adult reading research puts silent non-fiction reading near 238 words per minute; comfortable public speaking sits around 130-150. The same 1,500 word script is a six-minute read and an eleven-minute talk.",
    sections: [
      { h: "Choosing a words-per-minute figure", body: "Silent reading of fiction averages about 260 wpm, non-fiction 238, and technical or academic material closer to 180. Screen reading is roughly ten percent slower than print. If you publish a reading-time estimate on articles, 200-238 wpm is the honest range; anything above 300 flatters the reader and undercounts." },
      { h: "Speech pacing and the pause allowance", body: "Word count alone underestimates a talk because real delivery includes breaths, slide transitions, audience laughter and recovery from interruptions. Adding ten to fifteen percent covers normal pacing; a workshop with questions needs thirty percent or more. TED-style talks average around 163 wpm because the format rewards density." },
      { h: "Writing to a fixed time slot", body: "Work backwards: a ten-minute slot at 140 wpm with a ten percent pause allowance gives roughly 1,270 words of script. Cutting to fit late is painful, so draft to the target and read it aloud with a timer — silent estimates consistently run fifteen percent fast." },
    ],
    staticTable: {
      title: "Word count to time reference",
      head: ["Word count", "Silent read (238 wpm)", "Speech (140 wpm)", "Audiobook (155 wpm)", "Typical format"],
      rows: [
        [250, "1:03", "1:47", "1:37", "Short update"],
        [500, "2:06", "3:34", "3:14", "Newsletter section"],
        [750, "3:09", "5:21", "4:50", "Five-minute talk"],
        [1000, "4:12", "7:09", "6:27", "Standard blog post"],
        [1500, "6:18", "10:43", "9:41", "Conference talk"],
        [2500, "10:30", "17:51", "16:08", "Long-form article"],
        [5000, "21:00", "35:43", "32:16", "Whitepaper"],
      ],
    },
    faqs: [
      { q: "How long does it take to read 1,000 words?", a: "About four minutes and twelve seconds silently at the adult average of 238 words per minute, or just over seven minutes read aloud at a conversational 140 wpm." },
      { q: "How many words is a 10 minute presentation?", a: "Roughly 1,250-1,400 words at a comfortable speaking pace once pauses and slide transitions are included." },
      { q: "What reading speed should a blog use for estimates?", a: "Between 200 and 238 words per minute. Higher figures produce flattering but unrealistic estimates that erode trust when readers take twice as long." },
    ],
    related: ["word-count-to-pages-converter", "aspect-ratio-pixel-calculator", "youtube-ad-revenue-calculator", "timezone-meeting-overlap-finder"],
  },

  {
    slug: "social-engagement-rate-calculator",
    cluster: "digital",
    name: "Social Engagement Rate",
    h1: "Social Media Engagement Rate Calculator (Instagram, TikTok, X)",
    title: "Social Media Engagement Rate Calculator by Platform",
    description:
      "Calculate engagement rate by followers, by reach and by impressions, compare against platform benchmarks and estimate sponsored post value.",
    keywords: [
      "social media engagement rate calculator instagram tiktok",
      "what is a good engagement rate by follower count",
      "engagement rate by reach vs followers formula",
      "how much to charge for a sponsored post",
    ],
    inputs: [
      { id: "platform", label: "Platform", type: "select", def: "instagram", options: [
        { value: "instagram", label: "Instagram" },
        { value: "tiktok", label: "TikTok" },
        { value: "x", label: "X / Twitter" },
        { value: "youtube", label: "YouTube" },
        { value: "linkedin", label: "LinkedIn" },
      ] },
      { id: "followers", label: "Followers", type: "number", def: "24000", min: 1, step: 100 },
      { id: "reach", label: "Post reach (0 if unknown)", type: "number", def: "9500", min: 0, step: 100 },
      { id: "likes", label: "Likes", type: "number", def: "820", min: 0 },
      { id: "comments", label: "Comments", type: "number", def: "64", min: 0 },
      { id: "shares", label: "Shares / reposts", type: "number", def: "37", min: 0 },
      { id: "saves", label: "Saves", type: "number", def: "112", min: 0 },
    ],
    compute: (v) => {
      const f = Math.max(1, n(v, "followers", 1));
      const eng = n(v, "likes") + n(v, "comments") + n(v, "shares") + n(v, "saves");
      const erFollowers = (eng / f) * 100;
      const reach = n(v, "reach");
      const erReach = reach > 0 ? (eng / reach) * 100 : 0;
      const bench: Record<string, number> = { instagram: 1.9, tiktok: 4.7, x: 0.5, youtube: 3.2, linkedin: 2.6 };
      const p = s(v, "platform", "instagram");
      const target = bench[p] ?? 2;
      const cpm = { instagram: 10, tiktok: 8, x: 6, youtube: 20, linkedin: 25 }[p] ?? 10;
      const rate = (reach > 0 ? reach : f * 0.35) * (cpm / 1000) * (erFollowers / Math.max(target, 0.1));
      return {
        cards: [
          { label: "Engagement by followers", value: `${fmt(erFollowers, 2)}%`, sub: `Benchmark ${target}% on ${p}`, accent: true },
          { label: "Engagement by reach", value: reach > 0 ? `${fmt(erReach, 2)}%` : "Add reach", sub: "The metric brands actually ask for" },
          { label: "Total interactions", value: fmt(eng), sub: `${fmt(n(v, "saves"))} saves · ${fmt(n(v, "shares"))} shares` },
          { label: "Indicative post rate", value: money(Math.max(20, rate)), sub: "Engagement-weighted CPM estimate" },
        ],
        table: {
          title: "Interaction mix",
          head: ["Interaction", "Count", "Share of engagement", "Algorithmic weight"],
          rows: [
            ["Likes", fmt(n(v, "likes")), `${fmt((n(v, "likes") / Math.max(eng, 1)) * 100, 1)}%`, "Low"],
            ["Comments", fmt(n(v, "comments")), `${fmt((n(v, "comments") / Math.max(eng, 1)) * 100, 1)}%`, "High"],
            ["Shares", fmt(n(v, "shares")), `${fmt((n(v, "shares") / Math.max(eng, 1)) * 100, 1)}%`, "Highest"],
            ["Saves", fmt(n(v, "saves")), `${fmt((n(v, "saves") / Math.max(eng, 1)) * 100, 1)}%`, "Very high"],
          ],
        },
        summary: erFollowers >= target
          ? `You are above the ${p} benchmark of ${target}% — quote at the upper end of your rate range.`
          : `You are below the ${p} benchmark of ${target}% — shares and saves move this number fastest.`,
      };
    },
    intro:
      "Engagement rate is three different metrics wearing the same name. Rate by followers rewards small accounts and punishes large ones; rate by reach measures content quality independent of audience size; rate by impressions is the strictest of all. Brands increasingly ask for the reach-based figure, so this calculator prints both.",
    sections: [
      { h: "Which denominator to use", body: "Use followers when comparing accounts of similar size or when reach data is unavailable. Use reach when judging whether a specific post performed, because it removes the effect of algorithmic distribution. A post shown to twelve percent of your audience with a nine percent reach engagement rate is a strong post with a distribution problem, not a weak post." },
      { h: "Not all interactions are equal", body: "Platform ranking systems weight shares and saves far above likes, because both signal that content was worth keeping or passing on. A post with fifty saves and two hundred likes will typically outperform one with six hundred likes and no saves over the following seventy-two hours." },
      { h: "Engagement rate falls as you grow", body: "Median engagement declines predictably with follower count: micro accounts under ten thousand routinely see three to six percent on Instagram, while accounts over a million average under one percent. This is why brands pay premium CPMs for micro-influencers rather than for raw reach." },
    ],
    staticTable: {
      title: "Median engagement rate benchmarks",
      head: ["Follower tier", "Instagram", "TikTok", "X", "LinkedIn"],
      rows: [
        ["Under 5k (nano)", "4.8%", "9.4%", "1.2%", "5.1%"],
        ["5k-20k (micro)", "2.9%", "6.8%", "0.8%", "3.8%"],
        ["20k-100k (mid)", "1.9%", "4.7%", "0.5%", "2.6%"],
        ["100k-1m (macro)", "1.3%", "3.1%", "0.3%", "1.7%"],
        ["Over 1m (mega)", "0.9%", "2.4%", "0.2%", "1.1%"],
      ],
    },
    faqs: [
      { q: "What is a good engagement rate?", a: "It depends on size and platform. On Instagram, above three percent is strong for a micro account and above one and a half percent is strong above a hundred thousand followers. TikTok benchmarks run roughly double Instagram's." },
      { q: "Should engagement rate use reach or followers?", a: "Reach is the better quality signal because it removes distribution effects. Followers remains useful for cross-account comparison when reach data is private." },
      { q: "How much should I charge for a sponsored post?", a: "A common starting point is one hundred dollars per ten thousand followers, adjusted up or down by how far your engagement rate sits above or below the platform benchmark." },
    ],
    related: ["youtube-ad-revenue-calculator", "twitch-payout-estimator", "aspect-ratio-pixel-calculator", "side-hustle-profit-calculator"],
  },

  {
    slug: "file-download-time-calculator",
    cluster: "digital",
    name: "Download / Upload Time",
    h1: "File Download Time Calculator (GB over Mbps Connection Speed)",
    title: "File Download Time Calculator — GB over Mbps",
    description:
      "Calculate how long a file takes to download or upload at any connection speed, with real-world overhead applied and a speed comparison table.",
    keywords: [
      "file download time calculator gb mbps connection speed",
      "how long to download 50gb on 100 mbps",
      "megabits vs megabytes download speed conversion",
      "upload time calculator for large video files",
    ],
    inputs: [
      { id: "size", label: "File size", type: "number", def: "50", min: 0.01, step: 0.5 },
      { id: "unit", label: "Size unit", type: "select", def: "GB", options: [{ value: "MB", label: "MB" }, { value: "GB", label: "GB" }, { value: "TB", label: "TB" }] },
      { id: "speed", label: "Connection speed", type: "number", def: "100", unit: "Mbps", min: 0.1, step: 5 },
      { id: "overhead", label: "Real-world efficiency", type: "select", def: "0.85", options: [
        { value: "1", label: "Theoretical maximum (100%)" },
        { value: "0.92", label: "Wired ethernet (92%)" },
        { value: "0.85", label: "Good Wi-Fi (85%)" },
        { value: "0.65", label: "Congested or mobile (65%)" },
      ] },
    ],
    compute: (v) => {
      const mult = { MB: 1, GB: 1024, TB: 1024 * 1024 }[s(v, "unit", "GB")] ?? 1024;
      const megabytes = n(v, "size") * mult;
      const megabits = megabytes * 8;
      const effective = n(v, "speed") * n(v, "overhead", 0.85);
      const seconds = effective > 0 ? megabits / effective : 0;
      const hms = (sec: number) => {
        const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), ss = Math.round(sec % 60);
        return h > 0 ? `${h}h ${m}m ${ss}s` : m > 0 ? `${m}m ${ss}s` : `${ss}s`;
      };
      return {
        cards: [
          { label: "Transfer time", value: hms(seconds), sub: `at ${fmt(effective, 1)} Mbps effective`, accent: true },
          { label: "File size in megabits", value: `${fmt(megabits)} Mb`, sub: `${fmt(megabytes)} MB × 8` },
          { label: "Effective throughput", value: `${fmt(effective / 8, 2)} MB/s`, sub: `${fmt(effective, 1)} Mbps after overhead` },
          { label: "At theoretical max", value: hms(megabits / Math.max(n(v, "speed"), 0.1)), sub: "No overhead applied" },
        ],
        table: {
          title: "Same file at different connection speeds",
          head: ["Connection", "Speed", "Effective", "Transfer time"],
          rows: ([["ADSL", 10], ["Basic fibre", 50], ["Standard fibre", 100], ["Fast fibre", 300], ["Gigabit", 1000], ["5G mobile", 200]] as [string, number][])
            .map(([label, sp]) => [label, `${sp} Mbps`, `${fmt(sp * n(v, "overhead", 0.85), 1)} Mbps`, hms(megabits / (sp * n(v, "overhead", 0.85)))]),
        },
        summary: "Internet speeds are quoted in megabits, file sizes in megabytes — the factor of eight is the single most common source of confusion.",
      };
    },
    intro:
      "A 100 Mbps connection does not download 100 MB per second. Megabits are one eighth of a megabyte, so the theoretical ceiling is 12.5 MB per second, and TCP overhead, Wi-Fi contention and server-side throttling typically shave another ten to thirty-five percent off that in real conditions.",
    sections: [
      { h: "Bits, bytes and the factor of eight", body: "Internet service providers advertise in megabits per second (Mbps, lowercase b); operating systems display transfer rates in megabytes per second (MB/s, uppercase B). Divide the advertised figure by eight to get the best-case download rate: 100 Mbps is 12.5 MB/s, 1 Gbps is 125 MB/s." },
      { h: "Where the missing bandwidth goes", body: "Protocol overhead from TCP, IP and TLS headers consumes roughly three to five percent. Wi-Fi adds contention and retransmission losses of ten to fifteen percent on 5 GHz and considerably more on congested 2.4 GHz. Single-stream downloads are also frequently capped by the source server rather than by your line." },
      { h: "Upload is usually the bottleneck", body: "Most residential connections are asymmetric: a 500/50 package uploads at one tenth of its download rate. Backing up a 200 GB video project on 50 Mbps upload takes about nine hours at best. Symmetric fibre or scheduled overnight transfers are the practical fixes." },
    ],
    staticTable: {
      title: "Download time by file size and speed",
      head: ["File size", "10 Mbps", "50 Mbps", "100 Mbps", "500 Mbps", "1 Gbps"],
      rows: [
        ["1 GB", "13m 40s", "2m 44s", "1m 22s", "16s", "8s"],
        ["5 GB", "1h 8m", "13m 40s", "6m 50s", "1m 22s", "41s"],
        ["20 GB", "4h 33m", "54m 40s", "27m 20s", "5m 28s", "2m 44s"],
        ["50 GB", "11h 23m", "2h 17m", "1h 8m", "13m 40s", "6m 50s"],
        ["100 GB", "22h 45m", "4h 33m", "2h 17m", "27m 20s", "13m 40s"],
        ["1 TB", "9d 12h", "1d 22h", "23h 17m", "4h 39m", "2h 20s"],
      ],
      caption: "At 100% theoretical efficiency; expect ten to thirty-five percent longer in practice.",
    },
    faqs: [
      { q: "How long does it take to download 50GB on 100 Mbps?", a: "About one hour and eight minutes at the theoretical maximum, and closer to one hour and twenty minutes over good Wi-Fi once overhead is included." },
      { q: "Why is my download slower than my advertised speed?", a: "Protocol overhead, Wi-Fi losses, network congestion, and server-side limits. Wired ethernet on an uncongested line typically delivers ninety percent or more of the advertised rate." },
      { q: "Is Mbps the same as MB/s?", a: "No. Divide Mbps by eight to get MB/s. A 200 Mbps line peaks at 25 MB/s." },
    ],
    related: ["storage-unit-converter", "aspect-ratio-pixel-calculator", "appliance-electricity-cost-calculator", "reading-time-calculator"],
  },

  {
    slug: "eye-strain-20-20-20-timer",
    cluster: "digital",
    name: "20-20-20 Eye Strain Scheduler",
    h1: "20-20-20 Eye Strain Break Timer and Screen Time Scheduler",
    title: "20-20-20 Rule Eye Strain Break Scheduler",
    description:
      "Build a screen break schedule using the 20-20-20 rule, see total break minutes, blink deficit and a full daily timetable for your working hours.",
    keywords: [
      "20 20 20 eye strain break timer screen time scheduler",
      "how often should you look away from a screen",
      "digital eye strain break schedule for work",
      "computer vision syndrome prevention rule",
    ],
    inputs: [
      { id: "hours", label: "Screen hours per day", type: "number", def: "8", min: 1, max: 16, step: 0.5 },
      { id: "startHour", label: "Work starts at", type: "number", def: "9", unit: ":00", min: 0, max: 23 },
      { id: "interval", label: "Break interval", type: "number", def: "20", unit: "minutes", min: 10, max: 90, step: 5 },
      { id: "breaklen", label: "Break length", type: "number", def: "20", unit: "seconds", min: 10, max: 300, step: 10 },
      { id: "long", label: "Long break every", type: "number", def: "2", unit: "hours", min: 1, max: 4 },
    ],
    compute: (v) => {
      const hours = n(v, "hours", 8), iv = n(v, "interval", 20), bl = n(v, "breaklen", 20);
      const breaks = Math.floor((hours * 60) / iv);
      const breakMin = (breaks * bl) / 60;
      const longBreaks = Math.floor(hours / n(v, "long", 2));
      const blinkNormal = hours * 60 * 15;
      const blinkScreen = hours * 60 * 6;
      const rows: (string | number)[][] = [];
      const st = n(v, "startHour", 9);
      for (let i = 1; i <= Math.min(breaks, 14); i++) {
        const minsIn = i * iv;
        const h = Math.floor(st + minsIn / 60) % 24;
        const m = minsIn % 60;
        rows.push([`Break ${i}`, `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`, `${bl}s look at 20 ft`, minsIn % (n(v, "long", 2) * 60) === 0 ? "Long break — stand and walk 5 min" : "Micro break"]);
      }
      return {
        cards: [
          { label: "Breaks per day", value: fmt(breaks), sub: `Every ${iv} minutes`, accent: true },
          { label: "Total break time", value: `${fmt(breakMin, 1)} min`, sub: `${fmt((breakMin / (hours * 60)) * 100, 1)}% of screen time` },
          { label: "Long breaks", value: fmt(longBreaks), sub: `Every ${n(v, "long", 2)} hours` },
          { label: "Blink deficit", value: fmt(blinkNormal - blinkScreen), sub: "Missed blinks vs normal rate" },
        ],
        table: { title: "Your daily break timetable", head: ["#", "Time", "Action", "Type"], rows },
        summary: `Screen work cuts blink rate from about 15 to 6 per minute — the main driver of dry, gritty eyes by late afternoon.`,
      };
    },
    intro:
      "Digital eye strain is not caused by screen light so much as by two mechanical facts: sustained near focus keeps the ciliary muscle contracted for hours, and blink rate falls by roughly sixty percent when concentrating on a display. The 20-20-20 rule addresses both — twenty seconds of distance focus every twenty minutes relaxes accommodation and forces a blink reset.",
    sections: [
      { h: "Why twenty feet and twenty seconds", body: "At about six metres the eye's accommodation system reaches optical infinity and the ciliary muscle fully relaxes. Twenty seconds is roughly the time needed for that relaxation plus several complete blink cycles to redistribute the tear film. Shorter glances relax focus but do not restore tear coverage." },
      { h: "Beyond the twenty-minute cadence", body: "Ophthalmology guidance pairs the micro-break with a longer break every one to two hours where you stand, walk and look around a room. This addresses posture and dry eye simultaneously, since sustained forward head posture also reduces effective blink completeness." },
      { h: "Environment matters as much as timing", body: "Position the top of the monitor slightly below eye level so the upper lid covers more of the cornea, keep the screen sixty to seventy centimetres away, run humidity above forty percent, and avoid direct airflow from air conditioning or heating vents at face level." },
    ],
    staticTable: {
      title: "Screen hours and recommended break load",
      head: ["Daily screen hours", "20-min micro breaks", "Total micro-break time", "Long breaks", "Strain risk"],
      rows: [
        ["2 h", "6", "2 min", "1", "Low"],
        ["4 h", "12", "4 min", "2", "Low-moderate"],
        ["6 h", "18", "6 min", "3", "Moderate"],
        ["8 h", "24", "8 min", "4", "High"],
        ["10 h", "30", "10 min", "5", "High"],
        ["12 h", "36", "12 min", "6", "Very high"],
      ],
    },
    faqs: [
      { q: "What is the 20-20-20 rule?", a: "Every twenty minutes, look at something about twenty feet away for twenty seconds. It relaxes the focusing muscle and restores the tear film disrupted by reduced blinking." },
      { q: "Does the 20-20-20 rule actually work?", a: "Clinical studies show meaningful reductions in self-reported dryness and strain symptoms, though adherence is the limiting factor. A scheduled timer materially improves compliance over intention alone." },
      { q: "Do blue light glasses help with eye strain?", a: "Randomised trials have found little effect on strain symptoms. Break frequency, screen distance, ambient humidity and blink rate have far larger measurable effects." },
    ],
    related: ["sleep-cycle-rem-calculator", "caffeine-half-life-calculator", "reading-time-calculator", "business-days-calculator"],
  },

  {
    slug: "youtube-ad-revenue-calculator",
    cluster: "digital",
    name: "YouTube CPM Ad Revenue",
    h1: "YouTube Ad Revenue Calculator (CPM, RPM and Niche Estimator)",
    title: "YouTube Ad Revenue Calculator — CPM & RPM Estimator",
    description:
      "Estimate YouTube earnings from daily views using niche CPM ranges, the 55% creator share, monetised playback rate and a full RPM comparison table.",
    keywords: [
      "youtube ad revenue calculator cpm views niche estimator",
      "how much does youtube pay for 1 million views",
      "rpm vs cpm youtube difference explained",
      "highest paying youtube niches cpm 2026",
    ],
    inputs: [
      { id: "views", label: "Daily views", type: "number", def: "25000", min: 0, step: 500 },
      { id: "niche", label: "Content niche", type: "select", def: "12", options: [
        { value: "3", label: "Entertainment / vlogs — $3 CPM" },
        { value: "5", label: "Gaming — $5 CPM" },
        { value: "8", label: "Education / how-to — $8 CPM" },
        { value: "12", label: "Tech reviews — $12 CPM" },
        { value: "18", label: "Business / marketing — $18 CPM" },
        { value: "26", label: "Finance / insurance — $26 CPM" },
      ] },
      { id: "monetised", label: "Monetised playback rate", type: "number", def: "58", unit: "%", min: 5, max: 100 },
      { id: "geo", label: "Audience geography", type: "select", def: "1", options: [
        { value: "1.25", label: "Mostly US / CA / AU / UK" },
        { value: "1", label: "Mixed Tier-1 and Tier-2" },
        { value: "0.45", label: "Mostly Tier-2 / Tier-3" },
      ] },
    ],
    compute: (v) => {
      const views = n(v, "views");
      const cpm = n(v, "niche", 12) * n(v, "geo", 1);
      const monetised = views * (n(v, "monetised", 58) / 100);
      const gross = (monetised / 1000) * cpm;
      const creator = gross * 0.55;
      const rpm = views > 0 ? (creator / views) * 1000 : 0;
      return {
        cards: [
          { label: "Daily earnings", value: money(creator, "$", 2), sub: `55% creator share of $${fmt(cpm, 2)} CPM`, accent: true },
          { label: "Monthly earnings", value: money(creator * 30.44), sub: `${fmt(views * 30.44)} monthly views` },
          { label: "Annual earnings", value: money(creator * 365), sub: "Ad revenue only" },
          { label: "Your RPM", value: money(rpm, "$", 2), sub: "Revenue per 1,000 total views" },
        ],
        table: {
          title: "Earnings at different view levels",
          head: ["Views", "Monetised views", "Gross ad revenue", "Your 55% share"],
          rows: [10000, 100000, 500000, 1000000, 5000000].map((x) => {
            const mv = x * (n(v, "monetised", 58) / 100);
            const g = (mv / 1000) * cpm;
            return [fmt(x), fmt(mv), money(g), money(g * 0.55)];
          }),
        },
        summary: `RPM is always well below CPM because roughly ${fmt(100 - n(v, "monetised", 58))}% of views serve no ad and YouTube retains 45% of what remains.`,
      };
    },
    intro:
      "The number creators quote as 'CPM' is what advertisers pay per thousand monetised impressions. What lands in your account is RPM: gross revenue after YouTube's forty-five percent cut, divided across all views including those that never served an ad. The gap between the two is routinely a factor of three or four.",
    sections: [
      { h: "CPM, RPM and the monetised playback rate", body: "Only a fraction of views trigger an ad — ad blockers, mid-roll placement, kids content restrictions and viewer skips all reduce it. Typical monetised playback rates run between forty and seventy percent. Multiply views by that rate, divide by a thousand, multiply by CPM, then take fifty-five percent to reach creator revenue." },
      { h: "Why niche dominates everything else", body: "Advertiser demand varies enormously by topic. Personal finance, insurance, legal and B2B software regularly clear twenty-five dollar CPMs because a single converted customer is worth thousands. Entertainment and reaction content sits at two to four dollars because the audience carries no clear commercial intent." },
      { h: "Geography and seasonality", body: "US, Canadian, Australian and UK viewers monetise at several times the rate of most other markets. Layered on top is a strong seasonal cycle: Q4 CPMs run thirty to fifty percent above the annual average as retail budgets peak, then collapse in the first two weeks of January." },
    ],
    staticTable: {
      title: "Typical CPM and RPM by niche",
      head: ["Niche", "Advertiser CPM", "Typical creator RPM", "Revenue per 1M views"],
      rows: [
        ["Finance / insurance", "$22-$36", "$8-$14", "$8,000-$14,000"],
        ["Business / marketing", "$14-$24", "$5-$9", "$5,000-$9,000"],
        ["Tech reviews", "$9-$16", "$3.50-$6", "$3,500-$6,000"],
        ["Education / how-to", "$6-$11", "$2.50-$4.50", "$2,500-$4,500"],
        ["Gaming", "$3-$7", "$1.20-$2.80", "$1,200-$2,800"],
        ["Entertainment / vlogs", "$2-$5", "$0.90-$2", "$900-$2,000"],
        ["Shorts (all niches)", "n/a", "$0.05-$0.15", "$50-$150"],
      ],
    },
    faqs: [
      { q: "How much does YouTube pay for 1 million views?", a: "Between roughly $900 and $14,000 depending on niche and audience geography. A tech channel with a US-heavy audience typically lands around $4,000-$6,000 in ad revenue per million views." },
      { q: "What is the difference between CPM and RPM?", a: "CPM is what advertisers pay per thousand monetised impressions before YouTube's cut. RPM is your actual revenue per thousand total views after the 45% platform share and unmonetised views." },
      { q: "Do YouTube Shorts pay the same as long-form?", a: "No. Shorts revenue is pooled and shared, producing RPMs around five to fifteen cents per thousand views — roughly one twentieth of long-form rates." },
    ],
    related: ["twitch-payout-estimator", "social-engagement-rate-calculator", "side-hustle-profit-calculator", "aspect-ratio-pixel-calculator"],
  },

  {
    slug: "word-count-to-pages-converter",
    cluster: "digital",
    name: "Word Count to Printed Pages",
    h1: "Word Count to Printed Pages Converter (Font Size, Spacing and Margins)",
    title: "Word Count to Pages Converter — Font Size & Spacing",
    description:
      "Convert a word count into printed page count for any font size, line spacing and margin setting, with an academic and manuscript reference table.",
    keywords: [
      "word count to printed pages converter font margins",
      "how many pages is 2000 words double spaced",
      "1500 words is how many pages 12pt times new roman",
      "manuscript page count estimator",
    ],
    inputs: [
      { id: "words", label: "Word count", type: "number", def: "2000", min: 1, step: 100 },
      { id: "font", label: "Font size", type: "select", def: "12", options: [
        { value: "10", label: "10 pt" }, { value: "11", label: "11 pt" }, { value: "12", label: "12 pt" }, { value: "14", label: "14 pt" },
      ] },
      { id: "spacing", label: "Line spacing", type: "select", def: "2", options: [
        { value: "1", label: "Single" }, { value: "1.5", label: "1.5 lines" }, { value: "2", label: "Double" },
      ] },
      { id: "margin", label: "Margins", type: "select", def: "1", options: [
        { value: "0.85", label: "Narrow (1.27 cm)" }, { value: "1", label: "Normal (2.54 cm)" }, { value: "1.15", label: "Wide (3.8 cm)" },
      ] },
      { id: "family", label: "Typeface", type: "select", def: "1", options: [
        { value: "1", label: "Times New Roman" }, { value: "1.08", label: "Arial / Helvetica" }, { value: "1.14", label: "Courier New" }, { value: "0.96", label: "Calibri" },
      ] },
    ],
    compute: (v) => {
      const base = 500; // words per single-spaced 12pt normal-margin page
      const fontFactor = { 10: 1.35, 11: 1.16, 12: 1, 14: 0.75 }[n(v, "font", 12)] ?? 1;
      const perPage = (base * fontFactor) / (n(v, "spacing", 2) * n(v, "margin", 1) * n(v, "family", 1));
      const words = n(v, "words", 1);
      const pages = words / perPage;
      return {
        cards: [
          { label: "Printed pages", value: fmt(pages, 1), sub: `${fmt(perPage)} words per page`, accent: true },
          { label: "Words per page", value: fmt(perPage), sub: `${n(v, "font")} pt, ${n(v, "spacing")} spacing` },
          { label: "Characters (approx.)", value: fmt(words * 5.8), sub: "Including spaces" },
          { label: "Manuscript pages", value: fmt(words / 250, 1), sub: "Publishing standard 250 w/page" },
        ],
        table: {
          title: "Your word count across every setting",
          head: ["Spacing", "10 pt", "11 pt", "12 pt", "14 pt"],
          rows: ([["Single", 1], ["1.5 lines", 1.5], ["Double", 2]] as [string, number][]).map(([label, sp]) => [
            label,
            fmt(words / ((base * 1.35) / (sp * n(v, "margin", 1) * n(v, "family", 1))), 1),
            fmt(words / ((base * 1.16) / (sp * n(v, "margin", 1) * n(v, "family", 1))), 1),
            fmt(words / ((base * 1) / (sp * n(v, "margin", 1) * n(v, "family", 1))), 1),
            fmt(words / ((base * 0.75) / (sp * n(v, "margin", 1) * n(v, "family", 1))), 1),
          ]),
        },
        summary: "Page counts are estimates — headings, block quotes, figures and paragraph breaks all reduce words per page in practice.",
      };
    },
    intro:
      "Page counts are a function of five variables, not one: word count, font size, typeface metrics, line spacing and margins. The academic default of 12 pt Times New Roman, double spaced, 2.54 cm margins yields almost exactly 250 words per page — which is also, not coincidentally, the publishing industry's standard manuscript page.",
    sections: [
      { h: "Why typeface changes the count", body: "Courier New is monospaced and consumes roughly fourteen percent more horizontal space than Times New Roman at the same point size. Arial runs about eight percent wider, Calibri about four percent narrower. On a 5,000 word essay that difference is nearly two full pages." },
      { h: "Academic conventions worth knowing", body: "Most universities specify 12 pt, double spacing and 2.54 cm margins precisely so page limits are comparable across submissions. MLA and APA both assume roughly 250 words per double-spaced page. Where a limit is given in pages rather than words, 250 per page is the safe planning figure." },
      { h: "What the estimate cannot capture", body: "Headings, bulleted lists, tables, figures, block quotes and short final paragraphs all leave white space that no words-per-page formula accounts for. A structured report with subheadings every three hundred words typically runs ten to twenty percent longer than the raw estimate." },
    ],
    staticTable: {
      title: "Words to pages at standard settings",
      head: ["Word count", "Single 12 pt", "1.5 spacing", "Double 12 pt", "Typical document"],
      rows: [
        [500, "1", "1.5", "2", "Short essay"],
        [1000, "2", "3", "4", "Blog post / op-ed"],
        [1500, "3", "4.5", "6", "Undergraduate essay"],
        [2500, "5", "7.5", "10", "Seminar paper"],
        [5000, "10", "15", "20", "Dissertation chapter"],
        [10000, "20", "30", "40", "Long report"],
        [80000, "160", "240", "320", "Novel manuscript"],
      ],
    },
    faqs: [
      { q: "How many pages is 2,000 words double spaced?", a: "About eight pages in 12 pt Times New Roman with standard 2.54 cm margins — roughly 250 words per page." },
      { q: "How many words fit on a single-spaced page?", a: "Around 500 in 12 pt with normal margins, rising to about 675 at 10 pt and falling to about 375 at 14 pt." },
      { q: "Does the font choice really change page count?", a: "Yes. Courier New adds roughly fourteen percent to length versus Times New Roman at identical settings, which is why many style guides specify the typeface as well as the size." },
    ],
    related: ["reading-time-calculator", "storage-unit-converter", "aspect-ratio-pixel-calculator", "eye-strain-20-20-20-timer"],
  },

  {
    slug: "twitch-payout-estimator",
    cluster: "digital",
    name: "Twitch Payout Estimator",
    h1: "Twitch Streamer Payout Estimator (Subs, Bits and Ad Share)",
    title: "Twitch Payout Estimator — Subs, Bits & Ad Revenue",
    description:
      "Estimate monthly Twitch income from tier 1, 2 and 3 subscriptions, bits, ad revenue and the 50/50 or 70/30 split, with a payout threshold check.",
    keywords: [
      "twitch streamer payout estimator subscribers bits ad share",
      "how much does twitch pay per sub",
      "twitch bits to dollars conversion rate",
      "twitch 70 30 split eligibility calculator",
    ],
    inputs: [
      { id: "t1", label: "Tier 1 subs ($4.99)", type: "number", def: "180", min: 0 },
      { id: "t2", label: "Tier 2 subs ($9.99)", type: "number", def: "14", min: 0 },
      { id: "t3", label: "Tier 3 subs ($24.99)", type: "number", def: "4", min: 0 },
      { id: "split", label: "Revenue split", type: "select", def: "0.5", options: [
        { value: "0.5", label: "Standard affiliate/partner — 50%" },
        { value: "0.7", label: "Premium partner tier — 70%" },
      ] },
      { id: "bits", label: "Bits cheered per month", type: "number", def: "45000", min: 0, step: 1000 },
      { id: "hours", label: "Streaming hours per month", type: "number", def: "90", min: 1 },
      { id: "ccv", label: "Average concurrent viewers", type: "number", def: "320", min: 0 },
    ],
    compute: (v) => {
      const split = n(v, "split", 0.5);
      const subs = (n(v, "t1") * 4.99 + n(v, "t2") * 9.99 + n(v, "t3") * 24.99) * split;
      const bits = n(v, "bits") * 0.01;
      // Ads: roughly $3.50 CPM on Twitch, ~3 minutes of ads per hour per viewer.
      const adImpressions = (n(v, "ccv") * n(v, "hours") * 3) / 60;
      const ads = (adImpressions / 1000) * 3.5;
      const total = subs + bits + ads;
      return {
        cards: [
          { label: "Estimated monthly payout", value: money(total, "$", 2), sub: `${fmt(n(v, "t1") + n(v, "t2") + n(v, "t3"))} total subs`, accent: true },
          { label: "Subscription revenue", value: money(subs), sub: `${fmt(split * 100)}% split` },
          { label: "Bits revenue", value: money(bits), sub: `${fmt(n(v, "bits"))} bits at $0.01 each` },
          { label: "Ad revenue", value: money(ads), sub: "≈ $3.50 CPM, 3 min/hour" },
        ],
        bars: [
          { label: "Subs", value: subs, display: money(subs) },
          { label: "Bits", value: bits, display: money(bits) },
          { label: "Ads", value: ads, display: money(ads) },
        ],
        table: {
          title: "Revenue by subscription tier",
          head: ["Tier", "Count", "Viewer pays", "Your share", "Monthly total"],
          rows: [
            ["Tier 1", fmt(n(v, "t1")), "$4.99", money(4.99 * split, "$", 2), money(n(v, "t1") * 4.99 * split)],
            ["Tier 2", fmt(n(v, "t2")), "$9.99", money(9.99 * split, "$", 2), money(n(v, "t2") * 9.99 * split)],
            ["Tier 3", fmt(n(v, "t3")), "$24.99", money(24.99 * split, "$", 2), money(n(v, "t3") * 24.99 * split)],
            ["Prime subs", "—", "Free to viewer", money(2.5, "$", 2), "Counts as Tier 1"],
          ],
        },
        summary: total >= 100 ? "Above the $100 payout threshold — funds release on the standard 15-day net cycle." : "Below the $100 payout threshold; the balance rolls over to next month.",
      };
    },
    intro:
      "Twitch income has three independent streams with completely different economics. Subscriptions are recurring and split with the platform, bits are one-off and pay a flat cent each, and advertising depends on concurrent viewers rather than on followers. Most mid-size channels find subs contribute sixty to eighty percent of total revenue.",
    sections: [
      { h: "How the subscription split works", body: "The default affiliate and partner split is fifty-fifty, so a $4.99 tier one subscription returns $2.49. A premium seventy-thirty tier exists for a limited set of partners, usually capped at a revenue ceiling above which the split reverts. Prime subscriptions cost the viewer nothing extra and pay the streamer roughly the same as a tier one." },
      { h: "Bits and the cheer economy", body: "Bits convert at exactly one cent each to the streamer, while viewers pay a premium that varies by bundle size. Bits spike during events, charity streams and hype moments rather than accruing steadily, which makes them the least predictable line in any monthly forecast." },
      { h: "Why ad revenue is small for most channels", body: "Twitch ad CPMs sit around three to five dollars, and running more ads costs viewers. A channel averaging three hundred concurrent viewers over ninety hours generates roughly fourteen hundred impressions of ad time monthly — real money, but rarely more than ten percent of the total." },
    ],
    staticTable: {
      title: "Twitch revenue benchmarks by channel size",
      head: ["Avg concurrent viewers", "Typical subs", "Monthly subs revenue", "Bits + ads", "Total estimate"],
      rows: [
        ["10-25", "5-20", "$12-$50", "$10-$40", "$25-$90"],
        ["50-100", "30-90", "$75-$225", "$40-$150", "$115-$375"],
        ["150-300", "120-350", "$300-$875", "$150-$500", "$450-$1,375"],
        ["500-1,000", "500-1,500", "$1,250-$3,750", "$600-$2,000", "$1,850-$5,750"],
        ["2,000+", "2,500-8,000", "$6,250-$20,000", "$3,000-$12,000", "$9,250-$32,000"],
      ],
      caption: "Excludes sponsorships and donations, which often exceed platform revenue at larger sizes.",
    },
    faqs: [
      { q: "How much does Twitch pay per subscriber?", a: "$2.49 from a standard $4.99 tier one subscription under the fifty-fifty split, rising to $3.49 for partners on the seventy-thirty tier." },
      { q: "What are Twitch bits worth to the streamer?", a: "Exactly one US cent per bit. One hundred bits equals one dollar of streamer revenue regardless of what the viewer paid for the bundle." },
      { q: "When does Twitch pay out?", a: "Once your balance passes $100, on a net-15 cycle after the end of the month. Balances below the threshold roll forward." },
    ],
    related: ["youtube-ad-revenue-calculator", "social-engagement-rate-calculator", "side-hustle-profit-calculator", "freelance-hourly-rate-calculator"],
  },

  {
    slug: "appliance-electricity-cost-calculator",
    cluster: "digital",
    name: "Appliance Electricity Cost",
    h1: "Appliance Wattage Electricity Running Cost Calculator (kWh)",
    title: "Appliance Electricity Running Cost Calculator (kWh)",
    description:
      "Work out what any appliance costs to run per hour, day, month and year from its wattage and your electricity tariff, with standby cost and a wattage reference table.",
    keywords: [
      "appliance wattage electricity running cost calculator kwh",
      "how much does it cost to run an air conditioner per hour",
      "watts to kwh cost conversion formula",
      "standby power vampire load cost per year",
    ],
    inputs: [
      { id: "cur", label: "Currency", type: "select", def: "$", options: [{ value: "$", label: "USD $" }, { value: "£", label: "GBP £" }, { value: "€", label: "EUR €" }, { value: "A$", label: "AUD A$" }] },
      { id: "watts", label: "Appliance wattage", type: "number", def: "1500", unit: "W", min: 1 },
      { id: "hours", label: "Hours used per day", type: "number", def: "4", min: 0, max: 24, step: 0.5 },
      { id: "rate", label: "Electricity tariff", type: "number", def: "0.28", unit: "per kWh", min: 0.01, max: 2, step: 0.01 },
      { id: "standby", label: "Standby draw", type: "number", def: "2", unit: "W", min: 0, max: 60 },
    ],
    compute: (v) => {
      const c = s(v, "cur", "$");
      const kwhDay = (n(v, "watts") * n(v, "hours")) / 1000;
      const rate = n(v, "rate", 0.28);
      const standbyKwhYear = (n(v, "standby") * (24 - n(v, "hours")) * 365) / 1000;
      return {
        cards: [
          { label: "Cost per year", value: money(kwhDay * 365 * rate + standbyKwhYear * rate, c), sub: `${fmt(kwhDay * 365 + standbyKwhYear)} kWh annually`, accent: true },
          { label: "Cost per month", value: money(kwhDay * 30.44 * rate, c, 2), sub: `${fmt(kwhDay * 30.44, 1)} kWh` },
          { label: "Cost per hour", value: money((n(v, "watts") / 1000) * rate, c, 3), sub: `${fmt(n(v, "watts") / 1000, 3)} kWh per hour` },
          { label: "Standby cost per year", value: money(standbyKwhYear * rate, c, 2), sub: `${n(v, "standby")} W when idle` },
        ],
        table: {
          title: "Cost at different usage levels",
          head: ["Hours per day", "kWh per day", "Monthly cost", "Annual cost"],
          rows: [1, 2, 4, 6, 8, 12, 24].map((h) => {
            const k = (n(v, "watts") * h) / 1000;
            return [h, fmt(k, 2), money(k * 30.44 * rate, c, 2), money(k * 365 * rate, c)];
          }),
        },
        summary: `Every 100 W running continuously costs about ${money(0.1 * 24 * 365 * rate, c)} a year at your tariff.`,
      };
    },
    intro:
      "Electricity billing is simple arithmetic that almost nobody performs: watts divided by a thousand gives kilowatts, multiplied by hours gives kilowatt-hours, multiplied by your tariff gives money. The surprise is usually scale — a 1,500 W space heater run four hours a day through winter costs more than a fridge running continuously all year.",
    sections: [
      { h: "Nameplate wattage versus real draw", body: "The rating on the label is peak draw, not average. Thermostatically controlled appliances — fridges, air conditioners, heaters, ovens — cycle on and off, typically running thirty to sixty percent of the clock. For those, enter the hours the compressor or element actually runs, or use the annual kWh figure from the energy label instead." },
      { h: "Standby loads add up", body: "A typical household carries fifteen to thirty devices drawing one to five watts each while idle. That vampire load commonly totals fifty to a hundred watts continuously, which is five to ten percent of an average electricity bill for electricity that does nothing at all." },
      { h: "Tariff structures change the answer", body: "Flat-rate tariffs make this calculation exact. Time-of-use tariffs can triple the peak-period rate, so shifting the dishwasher, washing machine and EV charging to off-peak hours often saves more than replacing the appliance. Tiered tariffs raise the marginal rate once you cross a monthly threshold." },
    ],
    staticTable: {
      title: "Typical appliance wattage and running cost",
      head: ["Appliance", "Wattage", "Typical daily use", "kWh / year", "Annual cost at $0.28"],
      rows: [
        ["LED bulb", "9 W", "5 h", "16", "$4.60"],
        ["Laptop", "60 W", "8 h", "175", "$49"],
        ["Desktop + monitor", "250 W", "8 h", "730", "$204"],
        ["Refrigerator", "150 W (cycling)", "8 h effective", "438", "$123"],
        ["Washing machine", "500 W", "1 h", "183", "$51"],
        ["Tumble dryer", "3,000 W", "0.7 h", "767", "$215"],
        ["Air conditioner (1.5 t)", "1,500 W", "6 h", "3,285", "$920"],
        ["Electric space heater", "2,000 W", "4 h", "2,920", "$818"],
        ["Electric oven", "2,400 W", "0.5 h", "438", "$123"],
      ],
    },
    faqs: [
      { q: "How do I calculate the running cost of an appliance?", a: "Divide the wattage by 1,000 to get kilowatts, multiply by hours used, then multiply by your per-kWh tariff. A 1,500 W heater for four hours is 6 kWh, or $1.68 at 28 cents." },
      { q: "How much does standby power cost per year?", a: "A device drawing 3 W continuously uses about 26 kWh a year — around $7. Across twenty devices that is well over $100 annually." },
      { q: "Which appliances use the most electricity?", a: "Anything that heats or cools: tumble dryers, electric heaters, air conditioners, water heaters and ovens. Electronics are almost always trivial by comparison." },
    ],
    related: ["solar-panel-battery-size-calculator", "file-download-time-calculator", "ev-fuel-trip-cost-splitter", "rent-splitter-by-square-feet"],
  },

  {
    slug: "storage-unit-converter",
    cluster: "digital",
    name: "Data Storage Unit Converter",
    h1: "Data Storage Unit Converter (TB, GB, MB, PB — Binary vs Decimal)",
    title: "Data Storage Unit Converter — Binary vs Decimal Bytes",
    description:
      "Convert between bytes, KB, MB, GB, TB and PB in both decimal (1000) and binary (1024) bases, and see why a 1 TB drive shows as 931 GB.",
    keywords: [
      "data storage unit converter tb gb mb pb binary decimal",
      "why does my 1tb drive show 931gb",
      "gibibyte vs gigabyte difference explained",
      "bytes to terabytes conversion calculator",
    ],
    inputs: [
      { id: "value", label: "Value", type: "number", def: "1", min: 0.000001, step: 0.5 },
      { id: "from", label: "From unit", type: "select", def: "TB", options: ["B", "KB", "MB", "GB", "TB", "PB"].map((u) => ({ value: u, label: u })) },
      { id: "base", label: "Base", type: "select", def: "1000", options: [
        { value: "1000", label: "Decimal (SI) — 1 KB = 1000 B" },
        { value: "1024", label: "Binary (IEC) — 1 KiB = 1024 B" },
      ] },
    ],
    compute: (v) => {
      const units = ["B", "KB", "MB", "GB", "TB", "PB"];
      const base = n(v, "base", 1000);
      const idx = units.indexOf(s(v, "from", "TB"));
      const bytes = n(v, "value") * Math.pow(base, Math.max(0, idx));
      const other = base === 1000 ? 1024 : 1000;
      const conv = (b: number, bs: number, i: number) => b / Math.pow(bs, i);
      return {
        cards: [
          { label: "In bytes", value: fmt(bytes), sub: `${s(v, "value")} ${s(v, "from")} at base ${base}`, accent: true },
          { label: "As gigabytes", value: `${fmt(conv(bytes, base, 3), 3)} GB`, sub: `Base ${base}` },
          { label: "In the other base", value: `${fmt(conv(bytes, other, idx), 3)} ${s(v, "from")}`, sub: `Base ${other} reading` },
          { label: "Difference", value: `${fmt(Math.abs(1 - Math.pow(1000 / 1024, idx)) * 100, 2)}%`, sub: "Between decimal and binary at this scale" },
        ],
        table: {
          title: "Full conversion in both bases",
          head: ["Unit", "Decimal (1000)", "Binary (1024)", "IEC name"],
          rows: units.map((u, i) => [u, fmt(conv(bytes, 1000, i), i >= 3 ? 4 : 2), fmt(conv(bytes, 1024, i), i >= 3 ? 4 : 2), ["B", "KiB", "MiB", "GiB", "TiB", "PiB"][i]]),
        },
        summary: "Drive manufacturers use decimal; Windows reports binary — which is exactly why capacities appear to shrink after purchase.",
      };
    },
    intro:
      "Two incompatible conventions have coexisted for decades. Storage manufacturers use decimal SI prefixes where a gigabyte is 1,000,000,000 bytes; operating systems traditionally use binary powers where the same word means 1,073,741,824 bytes. The seven percent gap at gigabyte scale widens to nine percent at terabyte scale.",
    sections: [
      { h: "Why a 1 TB drive shows 931 GB", body: "The manufacturer sells one trillion bytes. Windows divides by 1024 three times, yielding 931.32 GiB, and labels the result 'GB'. Nothing is missing and nothing is faulty — the same bytes are simply being counted with a different divisor. macOS switched to decimal reporting in 2009 and shows the full 1 TB." },
      { h: "The IEC prefixes nobody adopted", body: "In 1998 the IEC standardised kibibyte, mebibyte and gibibyte for the binary values, reserving kilobyte and megabyte for decimal. Linux tooling and RAM specifications largely follow it; Windows, most consumer software and everyday speech do not. When precision matters, state the byte count explicitly." },
      { h: "Where each convention is used", body: "RAM is always binary, because memory addressing is inherently a power of two. Hard drives, SSDs and network speeds are decimal. File sizes reported by operating systems vary by platform. Cloud storage billing is almost universally decimal, which quietly favours the provider by a few percent." },
    ],
    staticTable: {
      title: "Decimal versus binary at every scale",
      head: ["Unit", "Decimal bytes", "Binary bytes", "Difference", "Advertised 1 unit shows as"],
      rows: [
        ["KB / KiB", "1,000", "1,024", "2.4%", "0.977 KiB"],
        ["MB / MiB", "1,000,000", "1,048,576", "4.9%", "0.954 MiB"],
        ["GB / GiB", "1,000,000,000", "1,073,741,824", "7.4%", "0.931 GiB"],
        ["TB / TiB", "10^12", "1,099,511,627,776", "10.0%", "0.909 TiB"],
        ["PB / PiB", "10^15", "2^50", "12.6%", "0.888 PiB"],
      ],
    },
    faqs: [
      { q: "Why does my 1TB drive only show 931GB?", a: "The manufacturer counts one trillion bytes in decimal; Windows divides by 1024 three times and reports 931 GiB while labelling it GB. No capacity is lost." },
      { q: "What is the difference between a gigabyte and a gibibyte?", a: "A gigabyte is 1,000,000,000 bytes; a gibibyte is 1,073,741,824 bytes — about 7.4% larger." },
      { q: "Which base do cloud providers use for billing?", a: "Almost all major providers bill in decimal gigabytes, so a 100 GB allowance is 100,000,000,000 bytes, not 107 billion." },
    ],
    related: ["file-download-time-calculator", "aspect-ratio-pixel-calculator", "word-count-to-pages-converter", "appliance-electricity-cost-calculator"],
  },
];